import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createFitTest } from '../../api/fitTests';
import { getFuncionarios } from '../../api/funcionarios';
import { getLocais } from '../../api/locais';
import { useAuth } from '../../contexts/AuthContext';
import { addOneYear, todayIso as todayStr } from '../../utils/date';
import { GRUPOS_TESTE } from '../../utils/fitTestOpcoes';

const TIPOS = [
  { key: 'respiratorio', label: 'Respiratório', icon: 'bi-lungs', color: '#198754', bg: '#f8fff9' },
  { key: 'auditivo',     label: 'Auditivo',     icon: 'bi-ear',   color: '#4361d8', bg: '#f8f9ff' },
];

export default function SolicitarAgendamento() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [funcionarios, setFuncionarios] = useState([]);
  const [locais, setLocais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    id_funcionario: searchParams.get('funcionario') || '',
    id_local: '',
    data_agendada: '',
    data_validade: '',
    observacoes: '',
    setor: '',
    funcao: '',
  });

  const [tipos, setTipos] = useState({ respiratorio: false, auditivo: false });

  // Apenas grupo_teste por tipo — sem equipamento
  const [grupoResp, setGrupoResp] = useState('qualitativo');
  const [grupoAud, setGrupoAud] = useState('objetivo');
  const [brigada, setBrigada] = useState(false);
  const [poco, setPoco] = useState(false);

  useEffect(() => {
    Promise.all([getFuncionarios(), getLocais()]).then(([f, loc]) => {
      setFuncionarios(f); setLocais(loc); setLoading(false);
    });
  }, []);

  // Auto-fill setor/funcao ao selecionar funcionário
  useEffect(() => {
    if (!form.id_funcionario || !user?.id_empresa) return;
    const func = funcionarios.find(f => f.id_funcionario === form.id_funcionario);
    const vinculo = func?.vinculos?.find(v => v.id_empresa === user.id_empresa);
    if (vinculo) {
      setForm(prev => ({
        ...prev,
        setor: vinculo.ultimo_setor || '',
        funcao: vinculo.ultima_funcao || '',
      }));
    }
  }, [form.id_funcionario, funcionarios, user]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const toggleTipo = (key) => setTipos(t => ({ ...t, [key]: !t[key] }));

  const handleAgendadaChange = (e) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, data_agendada: val, data_validade: addOneYear(val) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id_empresa) { setError('Seu usuário não está associado a uma empresa. Contate o administrador.'); return; }
    if (!tipos.respiratorio && !tipos.auditivo) { setError('Selecione pelo menos um tipo de teste'); return; }
    if (!form.data_agendada) { setError('Informe a data desejada para o teste'); return; }
    if (form.data_agendada < todayStr()) { setError('Não é permitido solicitar agendamento com data no passado'); return; }

    setError(''); setSaving(true);
    const comum = {
      id_funcionario: form.id_funcionario,
      id_empresa: user.id_empresa,
      id_local: form.id_local || null,
      data_agendada: form.data_agendada,
      data_validade: form.data_validade || null,
      observacoes: form.observacoes || null,
      setor: form.setor || null,
      funcao: form.funcao || null,
      // sem id_equipamento — fenix associa ao confirmar
    };
    try {
      const tasks = [];
      if (tipos.respiratorio) {
        tasks.push(createFitTest({
          ...comum,
          tipo_teste: 'respiratorio',
          grupo_teste: grupoResp,
          faz_parte_brigada: brigada,
          faz_parte_controle_poco: poco,
        }));
      }
      if (tipos.auditivo) {
        tasks.push(createFitTest({
          ...comum,
          tipo_teste: 'auditivo',
          grupo_teste: grupoAud,
        }));
      }
      await Promise.all(tasks);
      navigate('/fit-tests');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar agendamento');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0" onClick={() => navigate('/fit-tests')}>
        <i className="bi bi-arrow-left me-1"></i>Voltar
      </button>

      <PageHeader title="Solicitar Agendamento" subtitle="Informe os dados e a data desejada — a Fenix confirmará a disponibilidade" />

      <div className="card-datafit p-4" style={{ maxWidth: 640 }}>
        <div className="alert alert-info py-2 small mb-4">
          <i className="bi bi-info-circle me-2"></i>
          Após o envio, sua solicitação ficará <strong>Em análise</strong>. A equipe Fenix confirmará a data e definirá o equipamento.
        </div>

        {error && <div className="alert alert-danger py-2 small mb-3"><i className="bi bi-exclamation-triangle me-2"></i>{error}</div>}

        <form onSubmit={handleSubmit}>

          {/* ── Tipo(s) de teste ── */}
          <div className="mb-4">
            <label className="form-label small fw-500 d-block mb-2">Tipo(s) de teste *</label>
            <div className="d-flex gap-3">
              {TIPOS.map(({ key, label, icon, color, bg }) => (
                <div key={key} className="border rounded p-3 flex-fill text-center"
                  style={{ cursor: 'pointer', background: tipos[key] ? bg : '#fff', borderColor: tipos[key] ? color : '#dee2e6', borderWidth: tipos[key] ? 2 : 1, transition: 'all .15s', userSelect: 'none' }}
                  onClick={() => toggleTipo(key)}>
                  <i className={`bi ${icon} fs-3 d-block mb-1`} style={{ color: tipos[key] ? color : '#adb5bd' }} />
                  <div className="small fw-500" style={{ color: tipos[key] ? color : '#6c757d' }}>{label}</div>
                  {tipos[key] && <i className="bi bi-check-circle-fill d-block mt-1" style={{ color, fontSize: '.8rem' }} />}
                </div>
              ))}
            </div>
          </div>

          {/* ── Grupo respiratório ── */}
          {tipos.respiratorio && (
            <div className="border rounded p-3 mb-3" style={{ background: '#f8fff9', borderColor: '#b8e0c0' }}>
              <div className="small fw-bold text-uppercase mb-3" style={{ color: '#198754', letterSpacing: '.06em', fontSize: '.7rem' }}>
                <i className="bi bi-lungs me-1"></i>Respiratório
              </div>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label small fw-500">Grupo *</label>
                  <select className="form-select" value={grupoResp} onChange={e => setGrupoResp(e.target.value)}>
                    {GRUPOS_TESTE.respiratorio.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div className="col-12 d-flex gap-4">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="brigada" checked={brigada} onChange={e => setBrigada(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="brigada">Faz parte da brigada de incêndio</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="poco" checked={poco} onChange={e => setPoco(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="poco">Faz parte do controle de poço</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Grupo auditivo ── */}
          {tipos.auditivo && (
            <div className="border rounded p-3 mb-3" style={{ background: '#f8f9ff', borderColor: '#c5d0f0' }}>
              <div className="small fw-bold text-uppercase mb-3" style={{ color: '#4361d8', letterSpacing: '.06em', fontSize: '.7rem' }}>
                <i className="bi bi-ear me-1"></i>Auditivo
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-500">Grupo *</label>
                <select className="form-select" value={grupoAud} onChange={e => setGrupoAud(e.target.value)}>
                  {GRUPOS_TESTE.auditivo.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* ── Campos comuns ── */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-500">Funcionário *</label>
              <select className="form-select" value={form.id_funcionario} onChange={set('id_funcionario')} required>
                <option value="">Selecione...</option>
                {funcionarios.map(f => <option key={f.id_funcionario} value={f.id_funcionario}>{f.nome}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-500">Data desejada *</label>
              <input type="date" className="form-control" value={form.data_agendada}
                onChange={handleAgendadaChange} required min={todayStr()} />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-500">Setor</label>
              <input type="text" className="form-control" placeholder="Setor do funcionário"
                value={form.setor} onChange={set('setor')} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-500">Função</label>
              <input type="text" className="form-control" placeholder="Função do funcionário"
                value={form.funcao} onChange={set('funcao')} />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label small fw-500">Local de realização</label>
            <select className="form-select" value={form.id_local} onChange={set('id_local')}>
              <option value="">Sem local definido (opcional)</option>
              {locais.filter(l => !l.id_empresa || l.id_empresa === user?.id_empresa)
                .map(l => <option key={l.id_local} value={l.id_local}>{l.nome}{l.tipo ? ` — ${l.tipo}` : ''}</option>)}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label small fw-500">Data de validade</label>
            <input type="date" className="form-control" value={form.data_validade} readOnly
              style={{ background: '#f0f2f5', color: '#6c757d', cursor: 'default' }} />
          </div>

          <div className="mb-4">
            <label className="form-label small fw-500">Observações</label>
            <textarea className="form-control" rows={3} value={form.observacoes} onChange={set('observacoes')}
              placeholder="Informações adicionais para a equipe Fenix..." />
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn-datafit"
              disabled={saving || (!tipos.respiratorio && !tipos.auditivo)}>
              {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-send me-2"></i>}
              {tipos.respiratorio && tipos.auditivo ? 'Enviar 2 solicitações' : 'Enviar solicitação'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/fit-tests')}>Cancelar</button>
          </div>

        </form>
      </div>
    </>
  );
}
